import React from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const BijouxSurMesurePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <img 
          src="/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg"
          alt="Bague en or 18 carats avec diamants - Aupiho Joaillerie"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ position: 'fixed' }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide" style={{ fontFamily: 'MoonTime, cursive' }}>
              Bijoux sur mesure
            </h1>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-8 w-8 text-[#c69c70]" />
              <div className="w-12 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-xl text-white/90 font-light max-w-2xl mx-auto">
              Vous souhaitez un bijou personnalisé ? Créez-le sur mesure afin qu'il vous ressemble !
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-gray-700 text-lg leading-relaxed">
              Chez Aupiho Joaillerie, nous croyons que chaque bijou doit être aussi unique que la personne qui le porte. Nous vous proposons notre service de bijoux sur mesure, vous permettant de créer des pièces personnalisées qui reflètent parfaitement votre style et vos sentiments.
            </p>
          </div>
        </div>
      </section>

      {/* Un bijou unique pour chaque histoire */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-1 md:order-1">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/sur-mesure/bague-unique.jpg" 
                  alt="Bague sur mesure unique" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>

            <div className="order-2 md:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">Un bijou unique pour chaque histoire</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  Que ce soit pour une occasion spéciale comme un mariage, un anniversaire, une naissance, ou simplement pour vous faire plaisir, notre service de sur-mesure vous offre la possibilité de concevoir un bijou unique.
                </p>
                <p>
                  Vous pouvez choisir chaque détail de votre bijou : le design, le matériau (la couleur d'or 18 carats ou le platine), les pierres précieuses et les finitions.
                </p>
                <p>
                  Nos bijoux sont entièrement fabriqués en France dans nos ateliers partenaires de haute joaillerie. Chaque bijou est réalisé à la main par nos artisans vous garantissant une qualité et une finition irréprochable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pourquoi opter pour le sur-mesure ? */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">Pourquoi opter pour le sur-mesure ?</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  Un bijou sur-mesure est bien plus qu'un simple accessoire. C'est une œuvre d'art personnelle qui raconte une histoire et capture des souvenirs précieux.
                </p>
                <p>
                  Que vous souhaitiez créer une bague de fiançailles unique, un pendentif personnalisé ou des boucles d'oreilles spéciales, notre service de sur-mesure vous garantit une pièce qui vous ressemble et que vous chérirez pour toujours.
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/sur-mesure/bagues-doigts.jpg" 
                  alt="Bagues sur mesure portées" 
                  className="w-full h-full object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Des pierres précieuses personnelles ou transformer un ancien bijou */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-1 md:order-1">
              <div className="relative overflow-hidden rounded-2xl shadow-xl bg-white p-8">
                <img 
                  src="/assets/images/sur-mesure/croquis-bague.jpg" 
                  alt="Croquis et plans de bague sur mesure" 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            <div className="order-2 md:order-2">
              <h2 className="text-4xl font-light text-[#c69c70] mb-6 tracking-wide">Des pierres précieuses personnelles ou transformer un ancien bijou</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-base">
                <p>
                  Chez Aupiho Joaillerie, nous comprenons l'importance sentimentale des pierres précieuses que vous possédez déjà ou de transformer un ancien bijou qui vous est cher, que vous souhaitez moderniser ou adapter à votre goût.
                </p>
                <p>
                  Si vous avez des diamants ou d'autres pierres précieuses que vous souhaitez intégrer dans votre création sur-mesure, nous pouvons les utiliser pour concevoir un bijou unique qui conserve toute la valeur émotionnelle et l'histoire de vos pierres.
                </p>
                <p>
                  Nous pouvons aussi réutiliser l'or de vos anciens bijoux, des bijoux démodés, des bijoux cassés pour réaliser votre bijou sur mesure et lui donner une nouvelle vie en accord avec votre personnalité !
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xl text-gray-700 mb-8 font-light">
              Vous souhaitez faire une demande, réaliser un devis, vous avez des questions ?
            </p>
            <Link 
              to="/contactez-nous" 
              className="inline-block px-10 py-4 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-medium tracking-wider text-lg shadow-xl hover:shadow-2xl"
            >
              Contactez-nous
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BijouxSurMesurePage;

